import Link from 'next/link';
import React, { useCallback, useMemo } from 'react';

import { IPersonaFront } from '@api/routers/user/service';
import styled from '@emotion/styled';
import { Blockchain } from '@repo/prsn-blockchain';
import { useTranslations } from 'next-intl';
import { useBoolean } from 'usehooks-ts';

import { OPENSEA_PERSONA_ETH } from '@web/features/const';
import { useBridgeManager } from '@web/features/staking/BridgeManager';
import { Button, IconButton, Tooltip, UnaIcon } from '@web/ui/components';
import { useBulkActions } from '@web/ui/components/BulkAction/useBulkActions';
import { BridgeIcon, StakeIcon } from '@web/ui/icons';

import { StakeModal } from '../../modals/StakeModal';

import EmptyIconUnstaked from './empty-unstaked.svg';
import { StakingGrid } from './StakingGrid';
import { StakingPersonasEmpty } from './StakingPersonasEmpty';

const StyledAction = styled(Button)`
  white-space: nowrap;
`;

interface IUnstakedTokensGridProps {
  items: IPersonaFront[];
  hasStakedPersonas: boolean;
}

export const UnstakedTokensGrid = ({
  items,
  hasStakedPersonas,
}: IUnstakedTokensGridProps) => {
  const t = useTranslations();

  const { createInstance } = useBridgeManager();

  const {
    value: isStakeModalOpen,
    setTrue: openStakeModal,
    setFalse: closeStakeModal,
  } = useBoolean(false);

  const { selectedItems, itemClick, clearAll, selectItems } = useBulkActions();

  const selectedBlockchain = selectedItems[0]?.blockchain;
  const lockedItems = useMemo<IPersonaFront[]>(
    () =>
      selectedBlockchain
        ? items.filter((p) => p.blockchain !== selectedBlockchain)
        : [],
    [selectedBlockchain, items],
  );

  const handleStakeClick = useCallback(() => {
    if (selectedBlockchain === Blockchain.BASE) {
      openStakeModal();
    } else {
      createInstance({
        tokenIds: selectedItems.map((p) => p.tokenId),
        source: Blockchain.ETHEREUM,
        stake: true,
      });
    }
  }, [selectedItems, selectedBlockchain, openStakeModal, createInstance]);

  return (
    <>
      {items.length > 0 ? (
        <StakingGrid
          items={items}
          selectedItems={selectedItems}
          lockedItems={lockedItems}
          onSelectAll={() =>
            selectItems(
              items.filter((p) => p.blockchain === selectedBlockchain),
            )
          }
          onClearAll={clearAll}
          onItemClick={itemClick}
          bulkActions={[
            <Tooltip content={t('BULK_ACTION_TOOLTIP_BRIDGE')}>
              <IconButton
                onClick={() => {
                  if (selectedBlockchain && selectedItems.length > 0) {
                    createInstance({
                      tokenIds: selectedItems.map((item) => item.tokenId),
                      source: selectedBlockchain,
                    });
                  }
                }}
              >
                <BridgeIcon color="onBackground" />
              </IconButton>
            </Tooltip>,
            <Tooltip content={t('BULK_ACTION_TOOLTIP_STAKE')}>
              <IconButton onClick={handleStakeClick}>
                <StakeIcon color="onBackground" />
              </IconButton>
            </Tooltip>,
          ]}
        />
      ) : (
        <StakingPersonasEmpty
          icon={
            hasStakedPersonas ? <UnaIcon size="2500" /> : <EmptyIconUnstaked />
          }
          title={t(
            hasStakedPersonas
              ? 'STAKING_PERSONAS_UNSTAKED_EMPTY_HAS_STAKED_TITLE'
              : 'STAKING_PERSONAS_UNSTAKED_EMPTY_TITLE',
          )}
          subtitle={t(
            hasStakedPersonas
              ? 'STAKING_PERSONAS_UNSTAKED_EMPTY_HAS_STAKED_SUBTITLE'
              : 'STAKING_PERSONAS_UNSTAKED_EMPTY_SUBTITLE',
          )}
          action={
            hasStakedPersonas ? null : (
              <Link href={OPENSEA_PERSONA_ETH} rel="noreferrer" target="_blank">
                <StyledAction
                  label={t('STAKING_PERSONAS_UNSTAKED_EMPTY_ACTION')}
                />
              </Link>
            )
          }
        />
      )}

      <StakeModal
        personas={selectedItems}
        open={isStakeModalOpen}
        onClose={closeStakeModal}
      />
    </>
  );
};
